/** US-only outbound dial helpers. 10-digit NANP or +1 + 10 digits. */

export const UNREACHABLE_MESSAGE =
  "The other phone couldn't be reached. Check the number and try again.";

export const NOT_ROUTEABLE_MESSAGE =
  "Can't place that call. Use a 10-digit US number (example: 6575033066) or +1 and the 10 digits.";

export function tooFewDigitsMessage(n) {
  const count = Number(n);
  return `That's only ${count} digits. Enter the full 10-digit US number, then call.`;
}

export function digitsOnly(raw) {
  return String(raw || '').replace(/\D/g, '');
}

function isNanpNational(national) {
  // 10 digits, area code cannot start with 0 or 1. Do not require NXX
  // exchange rules — 555 test numbers and some real NXXes would fail.
  return /^[2-9]\d{9}$/.test(national || '');
}

/**
 * Split user input into a national digit string for US validation.
 * Leading country code 1 is stripped only when it is actually a country code
 * (+1… or 11-digit 1XXXXXXXXXX), not when the user typed 10 digits starting
 * with 1 (invalid NANP area code).
 */
export function usNationalDigits(raw) {
  const s = String(raw || '').trim();
  const digits = digitsOnly(s);
  if (!digits) return '';
  if (s.startsWith('+')) {
    if (digits.startsWith('1')) return digits.slice(1);
    return digits;
  }
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  return digits;
}

/**
 * Validate a US outbound destination.
 * @returns {{ ok: boolean, e164: string, error: string, nationalDigits: string }}
 */
export function validateUsDialNumber(raw) {
  const s = String(raw || '').trim();
  if (!s) {
    return { ok: false, e164: '', error: NOT_ROUTEABLE_MESSAGE, nationalDigits: '' };
  }
  if (/^sip:/i.test(s) || /truva-voice/i.test(s)) {
    return { ok: false, e164: '', error: NOT_ROUTEABLE_MESSAGE, nationalDigits: '' };
  }

  const digits = digitsOnly(s);
  const plus = s.startsWith('+');
  let national = '';

  if (plus) {
    if (!digits.startsWith('1')) {
      return { ok: false, e164: '', error: NOT_ROUTEABLE_MESSAGE, nationalDigits: digits };
    }
    national = digits.slice(1);
  } else if (digits.length === 11 && digits.startsWith('1')) {
    national = digits.slice(1);
  } else {
    national = digits;
  }

  if (!national.length) {
    return { ok: false, e164: '', error: NOT_ROUTEABLE_MESSAGE, nationalDigits: national };
  }
  if (national.length < 10) {
    return {
      ok: false,
      e164: '',
      error: tooFewDigitsMessage(national.length),
      nationalDigits: national,
    };
  }
  if (national.length === 10 && isNanpNational(national)) {
    return { ok: true, e164: `+1${national}`, error: '', nationalDigits: national };
  }
  return { ok: false, e164: '', error: NOT_ROUTEABLE_MESSAGE, nationalDigits: national };
}

/**
 * Destination for Call: prefer an explicit string override (Contacts /
 * Recents). Ignore synthetic click/pointer events. Otherwise read the
 * synchronous dial buffer — React `number` state can lag the last keypress.
 */
export function readDialDestination(overrideNumber, numberRef) {
  if (typeof overrideNumber === 'string' || typeof overrideNumber === 'number') {
    return String(overrideNumber).trim();
  }
  return String(numberRef?.current || '').trim();
}

/** Mutable dial buffer so Call can read digits without waiting for setState. */
export function createDialBuffer(initial = '') {
  let value = String(initial || '');
  return {
    get() {
      return value;
    },
    set(next) {
      value = String(next || '');
      return value;
    },
    append(digit) {
      value = `${value}${digit}`;
      return value;
    },
    backspace() {
      value = value.slice(0, -1);
      return value;
    },
  };
}
